import { __awaiter } from "tslib";
import { BrowserCacheLocation, EventType, PublicClientApplication, } from '@azure/msal-browser';
import axios from 'axios';
import { BehaviorSubject, Subject } from 'rxjs';
import { deleteCookie, getLoginOriginFromEnvironment, getTenantIdFromEnvironment, isB2CSessionActive, isSewLocation, setSessionCookie, } from './msal-helper';
export class MsalAuthService {
    constructor(clientId, redirectUri, postLogoutRedirectUri, language, country, environment, apiScopes, policy) {
        this.userUpdatedSubject = new Subject();
        this.loginStatusSubject = new BehaviorSubject(false);
        this.acquireTokenRedirectInProgress = false;
        this.queryParams = { country, language, origin: window.location.origin };
        policy = policy || 'B2C_1A_SEW_SUSI_HRD';
        const msalConfig = this.createMsalConfig(clientId, getTenantIdFromEnvironment(environment), redirectUri, postLogoutRedirectUri, policy, getLoginOriginFromEnvironment(environment));
        this.msalInstance = new PublicClientApplication(msalConfig);
        this.msalInstance.addEventCallback(event => {
            switch (event.eventType) {
                case EventType.LOGIN_SUCCESS:
                case EventType.SSO_SILENT_SUCCESS:
                    setSessionCookie('B2CSessionActive', 'X');
                    const authResult = event.payload;
                    this.msalInstance.setActiveAccount(authResult.account);
                    this.updateUserInfo(authResult.idTokenClaims);
                    this.loginStatusSubject.next(true);
                    break;
                case EventType.LOGOUT_SUCCESS:
                    deleteCookie('B2CSessionActive');
                    this.updateUserInfo(null);
                    this.msalInstance.clearCache();
                    this.loginStatusSubject.next(false);
                    break;
                default:
                    break;
            }
        });
        this.apiScopes = apiScopes;
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.msalInstance.initialize();
            try {
                const accounts = this.msalInstance.getAllAccounts();
                switch (accounts.length) {
                    case 0:
                        const redirectResult = yield this.msalInstance.handleRedirectPromise();
                        if (!redirectResult) {
                            try {
                                yield this.msalInstance.ssoSilent({
                                    scopes: this.apiScopes[0].scopes,
                                    state: this.apiScopes[0].api,
                                });
                            }
                            catch (_a) {
                                yield this.checkHRDLogin();
                            }
                        }
                        else {
                            this.msalInstance.setActiveAccount(redirectResult.account);
                        }
                        break;
                    case 1:
                        yield this.msalInstance.handleRedirectPromise();
                        this.msalInstance.setActiveAccount(accounts[0]);
                        break;
                    default:
                        console.info('Multiple accounts found:', accounts);
                }
            }
            catch (error) {
                console.error('Error during MSAL initialization:', error);
            }
            finally {
                this.loginStatusSubject.next(this.isLoggedIn());
            }
        });
    }
    isLoggedIn() {
        return this.msalInstance.getAllAccounts().length >= 1;
    }
    isLoggedInAsync() {
        if (this.isLoggedIn()) {
            return Promise.resolve(true);
        }
        return new Promise(resolve => {
            const sub = this.user$.subscribe(user => {
                if (user) {
                    sub.unsubscribe();
                    resolve(true);
                }
            });
        });
    }
    decodeJwt(token) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window
            .atob(base64)
            .split('')
            .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
            .join(''));
        return JSON.parse(jsonPayload);
    }
    login() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.msalInstance.loginRedirect({
                scopes: this.apiScopes[0].scopes,
                state: this.apiScopes[0].api,
                extraQueryParameters: this.queryParams,
            });
        });
    }
    logout() {
        return __awaiter(this, arguments, void 0, function* (baseUrl = '') {
            if (isSewLocation()) {
                sessionStorage.setItem('sso_disabled', 'true');
            }
            try {
                yield axios.get(`${baseUrl}/os/saml/logout`).catch(() => null);
            }
            catch (_a) {
            }
            yield this.msalInstance.logoutRedirect();
        });
    }
    getTokenForApi(api_1) {
        return __awaiter(this, arguments, void 0, function* (api, forceRefresh = false) {
            const apiData = this.apiScopes.find(apiScope => apiScope.api === api);
            if (!apiData) {
                return null;
            }
            try {
                const response = yield this.msalInstance.acquireTokenSilent({
                    scopes: apiData.scopes,
                    state: apiData.api,
                    forceRefresh: forceRefresh,
                });
                if (!response || !response.accessToken) {
                    return null;
                }
                return response.accessToken;
            }
            catch (error) {
                if (isB2CSessionActive() && !this.acquireTokenRedirectInProgress) {
                    this.acquireTokenRedirectInProgress = true;
                    yield this.msalInstance.acquireTokenRedirect({
                        scopes: apiData.scopes,
                        state: apiData.api,
                    });
                }
                return null;
            }
        });
    }
    getTokenFromCode(code) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const result = yield this.msalInstance.acquireTokenByCode({
                    code,
                });
                return result.accessToken;
            }
            catch (error) {
                console.error('Error acquiring token from code:', error);
                return null;
            }
        });
    }
    get user$() {
        return this.userUpdatedSubject.asObservable();
    }
    get loginStatus$() {
        return this.loginStatusSubject.asObservable();
    }
    createMsalConfig(clientId, tenantId, redirectUri, postLogoutRedirectUri, policy, origin) {
        return {
            auth: {
                clientId,
                authority: `${origin}/${tenantId}/${policy}`,
                knownAuthorities: [origin],
                redirectUri,
                postLogoutRedirectUri,
                navigateToLoginRequestUrl: true,
            },
            cache: {
                cacheLocation: BrowserCacheLocation.SessionStorage,
                storeAuthStateInCookie: true,
                secureCookies: true,
            },
            system: {
                windowHashTimeout: 60000,
                iframeHashTimeout: 6000,
                allowRedirectInIframe: true,
            },
        };
    }
    updateUserInfo(idTokenClaims) {
        if (!idTokenClaims) {
            this.userUpdatedSubject.next(null);
            return;
        }
        const sewUserClaims = {
            id: idTokenClaims.sub || undefined,
            displayName: idTokenClaims.displayName || undefined,
            firstName: idTokenClaims.given_name || idTokenClaims.firstName || undefined,
            lastName: idTokenClaims.family_name || idTokenClaims.lastName || undefined,
            mail: idTokenClaims.mail || undefined,
            contactId: idTokenClaims.contactId || undefined,
            country: idTokenClaims.country || undefined,
            language: idTokenClaims.language || undefined,
            sapUserId: idTokenClaims.userId || undefined,
            isEmployee: idTokenClaims.isEmployee || undefined,
            employee: idTokenClaims.isEmployee || undefined,
        };
        this.userUpdatedSubject.next(sewUserClaims);
    }
    checkHRDLogin() {
        return __awaiter(this, void 0, void 0, function* () {
            if (isSewLocation() && !sessionStorage.getItem('sso_disabled')) {
                try {
                    const result = yield this.msalInstance.loginPopup({
                        scopes: this.apiScopes[0].scopes,
                        state: this.apiScopes[0].api,
                        extraQueryParameters: this.queryParams,
                        domainHint: 'sew-eurodrive',
                    });
                    this.msalInstance.setActiveAccount(result.account);
                    this.updateUserInfo(result.idTokenClaims);
                    this.loginStatusSubject.next(true);
                }
                catch (_a) {
                    sessionStorage.setItem('sso_disabled', 'true');
                }
            }
            else {
                return undefined;
            }
        });
    }
}
